import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { MaskedField } from "../components/MaskedField";
import { CreditCard, Sparkles, RotateCw, Zap, Search, Activity, Shield, Pencil, Trash2, History, RefreshCw, Copy } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

const CARD_TYPES = ["Visa", "Mastercard", "Verve", "American Express", "Discover", "UnionPay"];

const EVENT_STYLE = {
  created: { label: "Created", cls: "bg-primary/15 text-primary", icon: Sparkles },
  used: { label: "Charged", cls: "bg-accent/15 text-accent", icon: Zap },
  rotated: { label: "Rotated", cls: "bg-amber-500/15 text-amber-500", icon: RotateCw },
};

const PAGE_SIZE = 6;

export default function CardVault() {
  const [cards, setCards] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [events, setEvents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [lastToken, setLastToken] = useState(null);
  const [search, setSearch] = useState("");
  const [useTarget, setUseTarget] = useState(null);
  const [useAmount, setUseAmount] = useState("");
  const [useLive, setUseLive] = useState(false);
  const [useCurrency, setUseCurrency] = useState("USD");
  const [useEmail, setUseEmail] = useState("");
  const [charging, setCharging] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [chargesTarget, setChargesTarget] = useState(null);
  const [chargesData, setChargesData] = useState(null);
  const [chargesLoading, setChargesLoading] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [form, setForm] = useState({
    card_number: "", expiry: "", holder_name: "", cvv: "", card_type: "Visa", approval_code: "", issuer_website: "", issuer_contact: "",
    billing_address: { street: "", city: "", state: "", postal_code: "", country: "United States" },
  });

  const load = (p = page, q = search) =>
    api
      .get("/cards", { params: { page: p, page_size: PAGE_SIZE, q } })
      .then((res) => {
        setCards(res.data.cards);
        setTotal(res.data.total);
      })
      .catch(() => {});
  const loadHistory = () => api.get("/history").then((res) => setEvents(res.data.events)).catch(() => {});

  useEffect(() => { loadHistory(); }, []);
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(1, search); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
  useEffect(() => { load(page, search); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const confirmUse = async () => {
    const amount = parseFloat(useAmount);
    if (!amount || amount <= 0) { toast.error("Enter a valid amount"); return; }
    setCharging(true);
    try {
      const res = await api.post(`/cards/${useTarget.id}/use`, {
        amount, live: useLive, currency: useCurrency, email: useEmail,
      });
      if (useLive) {
        toast.success(`Charged $${amount.toFixed(2)} live via processor — Card account credited (ref ${res.data.reference})`);
      } else {
        toast.success(`Recorded $${amount.toFixed(2)} charge to ${useTarget.token.slice(0, 12)}…`);
      }
      setUseTarget(null);
      setUseAmount("");
      setUseLive(false);
      setUseEmail("");
      loadHistory();
    } catch (err) {
      toast.error(useLive
        ? formatApiError(err.response?.data?.detail) || "Live charge failed — check your processor keys"
        : "Could not record charge");
    } finally {
      setCharging(false);
    }
  };

  const rotate = async (card) => {
    try {
      const res = await api.post(`/cards/${card.id}/rotate`);
      toast.success("Token rotated — old token retired");
      setLastToken({ token: res.data.token, card_mask: card.card_mask, brand: card.brand });
      load();
      loadHistory();
    } catch {
      toast.error("Could not rotate token");
    }
  };

  const startEdit = (c) => {
    setEditTarget(c);
    setEditForm({
      holder_name: c.holder_name, expiry: c.expiry, card_type: c.card_type || c.brand,
      approval_code: c.approval_code || "",
      issuer_website: c.issuer_website || "", issuer_contact: c.issuer_contact || "",
      billing_address: { ...{ street: "", city: "", state: "", postal_code: "", country: "United states" }, ...(c.billing_address || {}) },
    });
  };
  const saveEdit = async () => {
    try {
      await api.put(`/cards/${editTarget.id}`, editForm);
      toast.success("Card updated");
      setEditTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const confirmDelete = async () => {
    try {
      await api.delete(`/cards/${delTarget.id}`);
      toast.success("Token deleted");
      setDelTarget(null);
      load();
      loadHistory();
    } catch {
      toast.error("Could not delete token");
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setAddr = (k) => (e) => setForm({ ...form, billing_address: { ...form.billing_address, [k]: e.target.value } });

  const openCharges = async (card) => {
    setChargesTarget(card);
    setChargesData(null);
    setChargesLoading(true);
    try {
      const res = await api.get(`/cards/${card.id}/charges`);
      setChargesData(res.data);
    } catch {
      toast.error("Could not load charge history");
    } finally {
      setChargesLoading(false);
    }
  };

  const retryCharge = async (txnId) => {
    setRetryingId(txnId);
    try {
      const res = await api.post(`/transactions/${txnId}/retry`);
      if (res.data.status === "failed") {
        toast.error(`Retry failed: ${res.data.detail || "processor declined"}`);
      } else {
        toast.success("Charge retried successfully");
      }
      if (chargesTarget) await openCharges(chargesTarget);
      load();
      loadHistory();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setRetryingId(null);
    }
  };

  const tokenize = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post("/cards/tokenize", form);
      setLastToken(res.data);
      toast.success("Card tokenized");
      setForm({
        card_number: "", expiry: "", holder_name: "", cvv: "", card_type: "Visa", approval_code: "", issuer_website: "", issuer_contact: "",
        billing_address: { street: "", city: "", state: "", postal_code: "", country: "United states" },
      });
      load();
      loadHistory();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs font-mono uppercase tracking-wider text-primary">Tokenization Engine</div>
        <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">Card Vault & Tokens</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Swap raw card details for a reusable <span className="font-mono text-primary">tok_</span> token.
          The raw number is encrypted and never returned.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <form onSubmit={tokenize} className="rounded-xl border border-border bg-card p-6" data-testid="tokenize-form">
          <h3 className="mb-4 text-lg font-medium">Tokenize a card</h3>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Card number</Label>
              <Input data-testid="card-number-input" value={form.card_number} onChange={set("card_number")} required placeholder="4242 4242 4242 4242" inputMode="numeric" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Card type</Label>
                <Select value={form.card_type} onValueChange={(v) => setForm({ ...form, card_type: v })}>
                  <SelectTrigger data-testid="tok-card-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{CARD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>CVC / CVV</Label>
                <Input data-testid="tok-cvv-input" value={form.cvv} onChange={set("cvv")} required maxLength={4} placeholder="•••" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Expiry</Label>
                <Input data-testid="expiry-input" value={form.expiry} onChange={set("expiry")} required placeholder="12/28" />
              </div>
              <div className="space-y-1.5">
                <Label>Holder name</Label>
                <Input data-testid="holder-input" value={form.holder_name} onChange={set("holder_name")} required placeholder="Jane Doe" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Approval Code <span className="text-muted-foreground">(optional)</span></Label>
              <Input data-testid="tok-approval-code" value={form.approval_code} onChange={set("approval_code")} placeholder="e.g. 123456" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Card Issuers Website</Label>
                <Input data-testid="tok-issuer-website" value={form.issuer_website} onChange={set("issuer_website")} placeholder="https://bank.com (optional)" />
              </div>
              <div className="space-y-1.5">
                <Label>Issuers Contact</Label>
                <Input data-testid="tok-issuer-contact" value={form.issuer_contact} onChange={set("issuer_contact")} placeholder="Phone or email (optional)" />
              </div>
            </div>
            <div className="border-t border-border pt-4">
              <div className="mb-3 flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-muted-foreground">
                <Shield className="h-3.5 w-3.5" /> Billing address
              </div>
              <div className="space-y-3">
                <Input data-testid="tok-street" value={form.billing_address.street} onChange={setAddr("street")} placeholder="Street address" />
                <div className="grid grid-cols-2 gap-3">
                  <Input data-testid="tok-city" value={form.billing_address.city} onChange={setAddr("city")} placeholder="City" />
                  <Input data-testid="tok-state" value={form.billing_address.state} onChange={setAddr("state")} placeholder="State" />
                  <Input data-testid="tok-postal" value={form.billing_address.postal_code} onChange={setAddr("postal_code")} placeholder="Postal code" />
                  <Select value={form.billing_address.country} onValueChange={(v) => setForm({ ...form, billing_address: { ...form.billing_address, country: v } })}>
                    <SelectTrigger data-testid="tok-country"><SelectValue placeholder="Country" /></SelectTrigger>
                    <SelectContent>
                      {["United States", "Nigeria", "United Kingdom", "Canada", "Germany", "France", "Ghana", "Kenya", "South Africa", "Australia", "India", "Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-muted-foreground">
              <Shield className="h-4 w-4 text-primary" /> Card number and CVC/CVV are encrypted; the raw CVV is never shown again.
            </div>
            <Button type="submit" disabled={saving} data-testid="tokenize-card-btn" className="w-full">
              {saving ? "Tokenizing…" : "Generate Token"}
            </Button>
          </div>

          {lastToken && (
            <div className="mt-5 rounded-lg border border-primary/30 bg-primary/5 p-4" data-testid="token-result">
              <div className="mb-3 flex items-center gap-2 text-sm font-medium text-primary">
                <Sparkles className="h-4 w-4" /> Token generated
              </div>
              <div className="space-y-3">
                <MaskedField label="Token" value={lastToken.token} copyValue={lastToken.token} testid="new-token" />
                <MaskedField label={`${lastToken.brand} card`} value={lastToken.card_mask} testid="new-card-mask" />
              </div>
            </div>
          )}
        </form>

        <div className="rounded-xl border border-border bg-card p-6">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h3 className="text-lg font-medium">Stored tokens ({total})</h3>
          </div>
          <div className="relative mb-4">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="token-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search token, brand, holder or last 4…"
              className="pl-9"
            />
          </div>
          {cards.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-sm text-muted-foreground">
              <CreditCard className="h-8 w-8 opacity-40" />
              {search ? `No tokens match “${search}”.` : "No tokens yet. Tokenize a card to get started."}
            </div>
          ) : (
            <div className="space-y-3">
              {cards.map((c) => (
                <div key={c.id} className="rounded-lg border border-border bg-muted/30 p-4" data-testid={`card-row-${c.id}`}>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="rounded bg-secondary px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider">{c.card_type || c.brand}</span>
                    <span className="text-xs text-muted-foreground">{c.holder_name} · {c.expiry}</span>
                  </div>
                  <div className="space-y-2">
                    <MaskedField value={c.token} copyValue={c.token} testid={`token-${c.id}`} />
                    <div className="font-mono masked-dots text-sm text-muted-foreground">{c.card_mask}</div>
                    {c.approval_code && (
                      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid={`approval-code-${c.id}`}>
                        <span>Approval:</span>
                        <span className="font-mono text-foreground" data-testid={`approval-code-value-${c.id}`}>{c.approval_code}</span>
                        <button type="button" className="rounded p-0.5 transition-colors hover:bg-secondary hover:text-foreground" onClick={() => { navigator.clipboard.writeText(c.approval_code); toast.success("Approval code copied"); }} data-testid={`copy-approval-code-${c.id}`} aria-label="Copy approval code">
                          <Copy className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                    {(c.issuer_website || c.issuer_contact) && (
                      <div className="truncate text-[11px] text-muted-foreground" data-testid={`issuer-info-${c.id}`}>
                        Issuer: {c.issuer_website && <a href={/^https?:\/\//i.test(c.issuer_website) ? c.issuer_website : `https://${c.issuer_website}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid={`issuer-website-${c.id}`}>{c.issuer_website}</a>}
                        {c.issuer_website && c.issuer_contact && " · "}
                        {c.issuer_contact && <span data-testid={`issuer-contact-${c.id}`}>{c.issuer_contact}</span>}
                      </div>
                    )}
                    {(c.billing_address?.city || c.billing_address?.country) && (
                      <div className="text-[11px] text-muted-foreground">
                        {[c.billing_address?.city, c.billing_address?.country].filter(Boolean).join(", ")}
                      </div>
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => { setUseTarget(c); setUseAmount(""); setUseLive(false); setUseEmail(""); setUseCurrency("USD"); }} data-testid={`use-token-${c.id}`}>
                      <Zap className="mr-1.5 h-3.5 w-3.5" /> Use
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => rotate(c)} data-testid={`rotate-token-${c.id}`}>
                      <RotateCw className="mr-1.5 h-3.5 w-3.5" /> Rotate
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openCharges(c)} data-testid={`charges-token-${c.id}`} title="Charge history">
                      <History className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => startEdit(c)} data-testid={`edit-token-${c.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setDelTarget(c)} data-testid={`delete-token-${c.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between" data-testid="token-pagination">
              <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="tokens-prev">
                Previous
              </Button>
              <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
              <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="tokens-next">
                Next
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Token activity history */}
      <div className="rounded-xl border border-border bg-card p-6" data-testid="token-history">
        <h3 className="mb-4 flex items-center gap-2 text-lg font-medium">
          <Activity className="h-4 w-4 text-primary" /> Token activity ({events.length})
        </h3>
        {events.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No activity yet.</p>
        ) : (
          <div className="divide-y divide-border">
            {events.map((e) => {
              const s = EVENT_STYLE[e.type] || EVENT_STYLE.created;
              const Icon = s.icon;
              return (
                <div key={e.id} className="flex items-center gap-3 py-3" data-testid={`event-${e.id}`}>
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${s.cls}`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider ${s.cls}`}>{s.label}</span>
                      <span className="truncate font-mono text-xs text-muted-foreground">{e.token.slice(0, 18)}…</span>
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {e.brand} {e.card_mask}
                      {e.type === "used" && e.amount != null && ` · $${e.amount.toFixed(2)}`}
                      {e.type === "rotated" && e.old_token && ` · replaced ${e.old_token.slice(0, 12)}…`}
                    </div>
                  </div>
                  <span className="shrink-0 text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString()}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!useTarget} onOpenChange={(o) => !o && setUseTarget(null)}>
        <DialogContent data-testid="use-token-dialog">
          <DialogHeader>
            <DialogTitle>Charge with token</DialogTitle>
            <DialogDescription>Record a live charge, or run a real charge through your saved processor.</DialogDescription>
          </DialogHeader>
          {useTarget && (
            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-muted/30 p-3">
                <div className="font-mono text-sm">{useTarget.token.slice(0, 22)}…</div>
                <div className="font-mono masked-dots text-xs text-muted-foreground">{useTarget.brand} {useTarget.card_mask}</div>
              </div>
              <div className="space-y-1.5">
                <Label>Amount</Label>
                <Input
                  data-testid="use-amount-input"
                  type="number"
                  step="0.01"
                  value={useAmount}
                  onChange={(e) => setUseAmount(e.target.value)}
                  placeholder="49.99"
                  autoFocus
                />
              </div>
              <div className="flex items-start justify-between gap-3 rounded-lg border border-border p-3">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-1.5 text-sm font-medium">
                    <Zap className="h-3.5 w-3.5 text-primary" /> Live charge through processor
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Sends a real charge via your saved processor. Success credits your Card account and records the reference.
                  </p>
                </div>
                <Switch checked={useLive} onCheckedChange={setUseLive} data-testid="use-live-switch" />
              </div>
              {useLive && (
                <div className="grid grid-cols-2 gap-3" data-testid="use-live-fields">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Charge currency</Label>
                    <Select value={useCurrency} onValueChange={setUseCurrency}>
                      <SelectTrigger data-testid="use-currency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["USD", "NGN", "EUR", "GBP", "GHS", "KES", "ZAR"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Customer email</Label>
                    <Input data-testid="use-email" value={useEmail} onChange={(e) => setUseEmail(e.target.value)} placeholder="customer@citidan.com" />
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setUseTarget(null)}>Cancel</Button>
            <Button onClick={confirmUse} disabled={charging} data-testid="confirm-use-btn">
              {charging ? "Processing…" : useLive ? "Charge card" : "Record charge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent data-testid="edit-token-dialog">
          <DialogHeader>
            <DialogTitle>Edit tokenized card</DialogTitle>
            <DialogDescription>Update non-secret details. The card number and CVV stay encrypted.</DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Card type</Label>
                <Select value={editForm.card_type} onValueChange={(v) => setEditForm({ ...editForm, card_type: v })}>
                  <SelectTrigger data-testid="edit-card-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{CARD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Holder</Label><Input value={editForm.holder_name} onChange={(e) => setEditForm({ ...editForm, holder_name: e.target.value })} data-testid="edit-holder" /></div>
                <div className="space-y-1.5"><Label>Expiry</Label><Input value={editForm.expiry} onChange={(e) => setEditForm({ ...editForm, expiry: e.target.value })} data-testid="edit-expiry" /></div>
                <div className="col-span-2 space-y-1.5"><Label>Approval Code <span className="text-muted-foreground">(optional)</span></Label><Input value={editForm.approval_code} onChange={(e) => setEditForm({ ...editForm, approval_code: e.target.value })} placeholder="e.g. 123456" data-testid="edit-approval-code" /></div>
                <div className="space-y-1.5"><Label>Card Issuers Website</Label><Input value={editForm.issuer_website} onChange={(e) => setEditForm({ ...editForm, issuer_website: e.target.value })} placeholder="https://bank.com" data-testid="edit-issuer-website" /></div>
                <div className="space-y-1.5"><Label>Issuers Contact</Label><Input value={editForm.issuer_contact} onChange={(e) => setEditForm({ ...editForm, issuer_contact: e.target.value })} placeholder="Phone or email" data-testid="edit-issuer-contact" /></div>
              </div>
              <Label className="block pt-1">Billing address</Label>
              <div className="grid grid-cols-2 gap-3">
                <Input value={editForm.billing_address.city} onChange={(e) => setEditForm({ ...editForm, billing_address: { ...editForm.billing_address, city: e.target.value } })} placeholder="City" data-testid="edit-city" />
                <Input value={editForm.billing_address.country} onChange={(e) => setEditForm({ ...editForm, billing_address: { ...editForm.billing_address, country: e.target.value } })} placeholder="Country" data-testid="edit-country" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button onClick={saveEdit} data-testid="save-edit-token">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent data-testid="delete-token-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this token?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the tokenized card {delTarget?.card_mask}. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-token" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog open={!!chargesTarget} onOpenChange={(o) => !o && setChargesTarget(null)}>
        <DialogContent data-testid="charges-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Charge history</DialogTitle>
            <DialogDescription>
              Live charges processed against {chargesTarget?.brand} {chargesTarget?.card_mask}.
            </DialogDescription>
          </DialogHeader>
          {chargesLoading ? (
            <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>
          ) : !chargesData || chargesData.count === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-center text-sm text-muted-foreground">
              <Zap className="h-7 w-7 opacity-40" />
              No live charges yet. Toggle “Live charge” when using this card to charge a customer.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm" data-testid="charges-summary">
                <span className="text-muted-foreground">{chargesData.success_count} of {chargesData.count} successful</span>
                <span className="font-semibold text-primary">${chargesData.total_charged.toFixed(2)} captured</span>
              </div>
              <div className="max-h-80 divide-y divide-border overflow-y-auto">
                {chargesData.charges.map((ch) => {
                  const cls = ch.status === "completed" ? "bg-primary/15 text-primary"
                    : ch.status === "failed" ? "bg-destructive/15 text-destructive"
                    : "bg-muted text-muted-foreground";
                  return (
                    <div key={ch.id} className="flex items-center gap-3 py-3" data-testid={`charge-${ch.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`rounded px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider ${cls}`}>{ch.status}</span>
                          {ch.processor && <span className="text-[11px] capitalize text-muted-foreground">{ch.processor}</span>}
                        </div>
                        {ch.reference && <div className="mt-0.5 truncate font-mono text-[10px] text-muted-foreground/80">Ref: {ch.reference}</div>}
                        <div className="text-[11px] text-muted-foreground">{new Date(ch.created_at).toLocaleString()}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">${ch.amount.toFixed(2)} <span className="text-[10px] font-normal text-muted-foreground">{ch.currency}</span></div>
                        {ch.can_retry && (
                          <Button size="sm" variant="outline" className="mt-1 h-7 px-2 text-xs" disabled={retryingId === ch.id}
                            onClick={() => retryCharge(ch.id)} data-testid={`charge-retry-${ch.id}`}>
                            <RefreshCw className={`mr-1 h-3 w-3 ${retryingId === ch.id ? "animate-spin" : ""}`} /> Retry
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setChargesTarget(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
